import React, { useCallback, useRef, useState } from "react";
import { Button, Modal, Textarea } from "@mantine/core";

interface ConfirmOptions {
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface PromptOptions {
  title?: string;
  message: React.ReactNode;
  placeholder?: string;
  initialValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  required?: boolean;
  danger?: boolean;
}

type DialogState =
  | { kind: "closed" }
  | ({ kind: "confirm"; open: boolean } & ConfirmOptions)
  | ({ kind: "prompt"; open: boolean; value: string } & PromptOptions);

export function useConfirm(): {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  prompt: (options: PromptOptions) => Promise<string | null>;
  confirmModal: React.ReactElement;
} {
  const [state, setState] = useState<DialogState>({ kind: "closed" });
  const resolverRef = useRef<((value: never) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve as (value: never) => void;
      setState({ kind: "confirm", open: true, ...options });
    });
  }, []);

  const prompt = useCallback((options: PromptOptions): Promise<string | null> => {
    return new Promise<string | null>((resolve) => {
      resolverRef.current = resolve as (value: never) => void;
      setState({
        kind: "prompt",
        open: true,
        value: options.initialValue ?? "",
        ...options,
      });
    });
  }, []);

  const resolveAndClose = (result: unknown) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setState({ kind: "closed" });
    resolver?.(result as never);
  };

  let body: React.ReactNode = null;
  let title = "Please confirm";
  const opened = state.kind !== "closed" && state.open;

  if (state.kind === "confirm") {
    title = state.title ?? "Please confirm";
    body = (
      <>
        <div className="text-foreground mb-5 leading-relaxed">{state.message}</div>
        <div className="flex justify-end gap-2">
          <Button variant="default" radius="md" onClick={() => resolveAndClose(false)}>
            {state.cancelLabel ?? "Cancel"}
          </Button>
          <Button
            color={state.danger ? "red" : undefined}
            radius="md"
            onClick={() => resolveAndClose(true)}
          >
            {state.confirmLabel ?? "Confirm"}
          </Button>
        </div>
      </>
    );
  } else if (state.kind === "prompt") {
    title = state.title ?? "Please provide input";
    const currentValue = state.value;
    const required = state.required ?? false;
    const disabled = required && currentValue.trim().length === 0;
    body = (
      <>
        <div className="text-foreground mb-3 leading-relaxed">{state.message}</div>
        <Textarea
          value={currentValue}
          onChange={(e) =>
            setState((prev) =>
              prev.kind === "prompt" ? { ...prev, value: e.currentTarget.value } : prev,
            )
          }
          placeholder={state.placeholder}
          autosize
          minRows={3}
          classNames={{ input: "bg-input border-border text-foreground rounded-lg" }}
          data-autofocus
        />
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="default" radius="md" onClick={() => resolveAndClose(null)}>
            {state.cancelLabel ?? "Cancel"}
          </Button>
          <Button
            color={state.danger ? "red" : undefined}
            radius="md"
            disabled={disabled}
            onClick={() => resolveAndClose(currentValue)}
          >
            {state.confirmLabel ?? "Submit"}
          </Button>
        </div>
      </>
    );
  }

  const confirmModal = (
    <Modal
      opened={opened}
      onClose={() => resolveAndClose(state.kind === "prompt" ? null : false)}
      title={title}
      centered
      radius="md"
      overlayProps={{ backgroundOpacity: 0.5, blur: 2 }}
    >
      {body}
    </Modal>
  );

  return { confirm, prompt, confirmModal };
}
